import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const COLORS = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

/**
 * Revenue breakdown by industry showing ARR and Services OTR
 */
const IndustryPieChart = ({ data, viewMode = 'pipeline' }) => {
  const isPipeline = viewMode === 'pipeline';
  const title = isPipeline ? 'Pipeline Revenue by Industry' : 'Won Revenue by Industry';

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-slate-500">
          No {isPipeline ? 'pipeline' : 'won'} data available for the selected period
        </div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (!value) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white px-3 py-2 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-medium text-slate-800">{item.industry}</p>
          <p className="text-sm text-blue-600">ARR: {formatCurrency(item.arr)}</p>
          <p className="text-sm text-purple-600">Services: {formatCurrency(item.servicesOTR)}</p>
          <p className="text-sm text-slate-600">Quotes: {item.count}</p>
        </div>
      );
    }
    return null;
  };

  // Calculate totals
  const totalARR = data.reduce((sum, d) => sum + (d.arr || 0), 0);
  const totalServices = data.reduce((sum, d) => sum + (d.servicesOTR || 0), 0);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        {/* Pie Chart */}
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="arr"
                nameKey="industry"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Industry Table */}
        <div className="overflow-auto max-h-48">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left py-1 px-1 font-semibold text-slate-600">Industry</th>
                <th className="text-right py-1 px-1 font-semibold text-blue-600">ARR</th>
                <th className="text-right py-1 px-1 font-semibold text-purple-600">Services</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.industry} className="border-b border-slate-100">
                  <td className="py-1 px-1">
                    <div className="flex items-center gap-1">
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="truncate" title={item.industry}>{item.industry}</span>
                    </div>
                  </td>
                  <td className="py-1 px-1 text-right text-blue-600">{formatCurrency(item.arr)}</td>
                  <td className="py-1 px-1 text-right text-purple-600">{formatCurrency(item.servicesOTR)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold">
              <tr>
                <td className="py-1 px-1 text-slate-800">Total</td>
                <td className="py-1 px-1 text-right text-blue-700">{formatCurrency(totalARR)}</td>
                <td className="py-1 px-1 text-right text-purple-700">{formatCurrency(totalServices)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IndustryPieChart;
